import { List } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import * as PodcastActions from '../../../../actions';
import * as PodcastConstants from '../../../../constants';
import * as D from '../../../../deps';
import * as PodcastUtils from '../../../../utils';
import BackstagePodcastSortableSectionHeader from '../../../common/BackstagePodcastSortableSectionHeader/BackstagePodcastSortableSectionHeader';

import BackstagePodcastProgramRecentEpisodesContent from './BackstagePodcastProgramRecentEpisodesContent';

const emptyList = List();
export class BackstagePodcastRecentEpisodes extends React.Component {
  static propTypes = {
    programId: PropTypes.string,
    newestEpisodes: ImmutablePropTypes.listOf(PropTypes.string),
    oldestEpisodes: ImmutablePropTypes.listOf(PropTypes.string),
    totalEpisodeCount: PropTypes.number,
    allEpisodesUrl: PropTypes.string,
    requestGetDetails: PropTypes.func,
    initialSortingOrder: PropTypes.string,
    hasFetchedData: PropTypes.bool,
    isMobileDevice: PropTypes.bool,
  };

  static getDerivedStateFromProps(props, state) {
    if (!state.currentSortingOrder && props.initialSortingOrder) {
      return {
        currentSortingOrder: props.initialSortingOrder,
      };
    }
    return null;
  }

  state = {
    currentSortingOrder: null,
  };

  static contextType = D.Utils.Context.AppContext;

  onSortChange = sortingOrder => {
    if (this.props.programId && sortingOrder !== this.state.currentSortingOrder) {
      this.props.requestGetDetails({
        pandoraId: this.props.programId,
        sortingOrder,
      });
      this.setState({ currentSortingOrder: sortingOrder });
    }

    this.context.integration.action.clicked({
      pandoraId: this.props.programId,
      contentSection: D.Constants.ContentSections.BackstageContentSections.EPISODE_LIST,
      componentEventType: PodcastConstants.ComponentEventTypes[sortingOrder],
    });
  };

  onSeeAllEpisodesClick = () => {
    this.context.integration.action.clicked({
      pandoraId: this.props.programId,
      contentSection: D.Constants.ContentSections.BackstageContentSections.EPISODE_LIST,
    });
  };

  getRecentEpisodesList = () => {
    const { hasFetchedData, newestEpisodes, oldestEpisodes } = this.props;
    const { currentSortingOrder } = this.state;

    if (!hasFetchedData || !currentSortingOrder) {
      return emptyList;
    } else if (currentSortingOrder === PodcastConstants.AllEpisodesSortTypes.NEWEST_TO_OLDEST) {
      return newestEpisodes;
    } else if (currentSortingOrder === PodcastConstants.AllEpisodesSortTypes.OLDEST_TO_NEWEST) {
      return oldestEpisodes;
    }

    return emptyList;
  };

  render() {
    const { totalEpisodeCount, allEpisodesUrl, initialSortingOrder, isMobileDevice } = this.props;

    return (
      <D.ContentSection>
        <BackstagePodcastSortableSectionHeader
          leftColumn={<FormattedMessage {...PodcastConstants.messages.episodesHeader} />}
          onSortChange={this.onSortChange}
          isMobileDevice={isMobileDevice}
          initialSortingOrder={initialSortingOrder}
        />
        <BackstagePodcastProgramRecentEpisodesContent
          recentEpisodes={this.getRecentEpisodesList()}
          totalEpisodeCount={totalEpisodeCount}
        />
        {totalEpisodeCount > PodcastConstants.PODCAST_RECENT_EPISODE_MAX_LIST_SIZE && (
          <D.ActionRow
            url={allEpisodesUrl}
            onClick={this.onSeeAllEpisodesClick}
            mainText={<FormattedMessage {...PodcastConstants.messages.seeAllEpisodes} />}
            secondText={
              <FormattedMessage
                {...D.Constants.Podcasts.messages.episodeCount}
                values={{ count: totalEpisodeCount }}
              />
            }
            isMobileDevice={isMobileDevice}
          />
        )}
      </D.ContentSection>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const { programId } = ownProps;

  const annotation = D.getAnnotationsById(state, programId);
  const programTitleUrl = D.Utils.Core.getFromMap(annotation, 'shareableUrlPath');
  const allEpisodesUrl = programTitleUrl
    ? D.Utils.Route.getBackstagePodcastProgramAllEpisodesPath(programTitleUrl)
    : null;
  const totalEpisodeCount = D.Utils.Core.getFromMap(annotation, 'episodeCount');

  const userProgramDetails = PodcastUtils.getPodcastProgramDetailsById(state, programId);
  const initialSortingOrder = userProgramDetails.get('sortingOrder');
  const newestEpisodes = userProgramDetails.getIn(
    ['recentEpisodes', PodcastConstants.AllEpisodesSortTypes.NEWEST_TO_OLDEST],
    null,
  );
  const oldestEpisodes = userProgramDetails.getIn(
    ['recentEpisodes', PodcastConstants.AllEpisodesSortTypes.OLDEST_TO_NEWEST],
    null,
  );
  const areEpisodesAnnotated =
    PodcastUtils.areEpisodesIdsAnnotated(state, newestEpisodes) ||
    PodcastUtils.areEpisodesIdsAnnotated(state, oldestEpisodes);
  const hasFetchedData =
    areEpisodesAnnotated ||
    !D.Utils.Api.isRequestInFlight(state, {
      endpoint: D.Constants.Api.ApiEndpoints.AESOP_GET_DETAILS,
    });
  const isMobileDevice = D.isMobileDevice(state);

  return {
    newestEpisodes,
    oldestEpisodes,
    allEpisodesUrl,
    totalEpisodeCount,
    initialSortingOrder,
    hasFetchedData,
    isMobileDevice,
  };
}

const mapDispatchToProps = {
  requestGetDetails: PodcastActions.requestGetDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstagePodcastRecentEpisodes);
